import { hasBeacon, hasKeepalive, hasWindow, joinPath } from '../util/env.js';
import { buildAuthHeaders } from './auth.js';
import type { PayloadQueue } from './queue.js';
import type { LogPayload, ResolvedLoggerOptions } from '../types.js';

export interface UnloadFlushHandle {
  uninstall: () => void;
}

export function installUnloadFlush(
  options: ResolvedLoggerOptions & { fetch: typeof fetch },
  queue: PayloadQueue,
): UnloadFlushHandle | null {
  if (!hasWindow()) return null;
  const url = joinPath(options.baseUrl, options.endpointPath);
  let installed = false;

  const flushNow = (): void => {
    const { items } = queue.drain();
    if (items.length === 0) return;
    const usingKeepalive = hasKeepalive();
    if (usingKeepalive) {
      void sendKeepaliveBatch(options, url, items);
    } else if (hasBeacon()) {
      sendBeaconBatch(url, items);
    }
  };

  const onPageHide = (): void => flushNow();
  const onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      flushNow();
    }
  };

  window.addEventListener('pagehide', onPageHide);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }
  installed = true;

  return {
    uninstall: () => {
      if (!installed) return;
      installed = false;
      window.removeEventListener('pagehide', onPageHide);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    },
  };
}

async function sendKeepaliveBatch(
  options: ResolvedLoggerOptions & { fetch: typeof fetch },
  url: string,
  payloads: LogPayload[],
): Promise<void> {
  let headers: Record<string, string>;
  try {
    headers = await buildAuthHeaders(options);
  } catch {
    headers = {};
  }
  headers['Content-Type'] = 'application/json';

  for (const payload of payloads) {
    const body = JSON.stringify(payload);
    if (body.length >= 64 * 1024) continue;
    try {
      await options.fetch(url, {
        method: 'POST',
        headers,
        body,
        credentials: options.credentials,
        keepalive: true,
      });
    } catch {
      /* unload best-effort */
    }
  }
}

function sendBeaconBatch(url: string, payloads: LogPayload[]): void {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  for (const payload of payloads) {
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } catch {
      /* ignore */
    }
  }
}
